import { Map } from "immutable";
import * as React from 'react';
import ResizeObserverPolyfill from '@juggle/resize-observer';

import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import rafSchedule from 'raf-schd';

import {
    NoteColor,
    NOTE_SIZE,
    SLIDE_WIDTH,
    SLIDE_HEIGHT, SLIDE_CONTENT_HEIGHT,
} from '../constants';
import {
    DraggingState,
    Note,
    SelectorDraggingState,
    ShapeDraggingState,
    SlideData,
    TemplateData,
} from '../reducers';

import NoteDefs from './NoteDefs';
import NoteView from './NoteView';
import Slide from '../containers/Slide';
import StoryboardTemplateView from './StoryboardTemplateView';
import { PanningState } from "../reducers/ui-state";


interface Props {
    readonly width: number,
    readonly height: number,
    readonly notes: ReadonlyArray<Note>,
    readonly slides: ReadonlyArray<SlideData>,
    readonly zoomLevel: number,
    readonly scrollLeft: number,
    readonly scrollTop: number,
    readonly panning: PanningState | null,
    readonly noteDragging: DraggingState | null,
    readonly shapeDragging: ShapeDraggingState | null,
    readonly selectorDragging: SelectorDraggingState | null,
    readonly slideDragging: DraggingState | null,
    readonly sketchState: DraggingState | null,
    readonly templateData: TemplateData,
    readonly textFields: Map<string, string>,
    readonly templateTextSupplier: (id: string) => string,
    readonly onStartPanning: (mouseX: number, mouseY: number) => void,
    readonly onPanning: (mouseX: number, mouseY: number) => void,
    readonly onStopPanning: (mouseX: number, mouseY: number) => void,
    readonly onNoteStartDragging: (uuid: string, x: number, y: number) => void,
    readonly onNoteDragging: (x: number, y: number) => void,
    readonly onNoteStopDragging: (x: number, y: number) => void,
    readonly onNoteDoubleClicked: (uuid: string, text: string, color: NoteColor) => void,
    readonly onNoteDeleteButtonClicked: (uuid: string) => void,
    readonly onShapeSketching: (x: number, y: number) => void,
    readonly onShapeStopSketching: (x: number, y: number) => void,
    readonly onShapeDragging: (x: number, y: number) => void,
    readonly onShapeStopDragging: (x: number, y: number) => void,
    readonly onSelectorDragging: (x: number, y: number) => void,
    readonly onSelectorStopDragging: (x: number, y: number) => void,
    readonly onSlideDragging: (x: number, y: number) => void,
    readonly onSlideStopDragging: (x: number, y: number) => void,
    readonly onBackgroundDoubleClicked: () => void,
    readonly onTemplateHelpClicked: (content: string) => void,
    readonly onTextFieldClicked: (key: string, text: string) => void,
    readonly onCanvasClicked: () => void,
    readonly onScrolled: (scrollLeft: number, scrollRight: number) => void,
    readonly onStoryboardResized: (width: number, height: number) => void,
}


const styles = (theme: Theme) => createStyles({
    root: {
        backgroundColor: theme.palette.grey[700],
        width: '100%',
        height: '100%',
        overflow: 'auto',
    },
});

type Styles = 'root';


function stopPropagation(event: React.MouseEvent<SVGElement>) {
    event.stopPropagation();
    event.preventDefault();
}


class Storyboard extends React.Component<Props & WithStyles<Styles>> {

    private svg: any;
    private background: HTMLElement | null;
    private resizeObserver: any;
    private readonly scheduleMouseMove: ((clientX: number, clientY: number) => void) & { cancel: () => void };
    private readonly scheduleScroll: (() => void) & { cancel: () => void };

    constructor(props: Props & WithStyles<Styles>) {
        super(props);
        this.scheduleMouseMove = rafSchedule(
            (clientX: number, clientY: number) => {
                const { noteDragging, slideDragging, sketchState, shapeDragging, selectorDragging, panning } = this.props;
                if (noteDragging) {
                    const { width, height, onNoteDragging, onNoteStopDragging } = this.props;
                    const localPoint = this.calculateLocalPoint(clientX, clientY);
                    const x = localPoint.x + noteDragging.offsetX;
                    const y = localPoint.y + noteDragging.offsetY;
                    if (x < -NOTE_SIZE / 4 || x > width - NOTE_SIZE / 2 || y < -NOTE_SIZE / 4 || y > height - NOTE_SIZE / 2) {
                        onNoteStopDragging(
                            Math.max(-NOTE_SIZE / 4, Math.min(x, width - NOTE_SIZE / 2)),
                            Math.max(-NOTE_SIZE / 4, Math.min(y, height - NOTE_SIZE / 2))
                        );
                    } else {
                        onNoteDragging(x, y);
                    }
                } else if (slideDragging) {
                    const { width, height, onSlideDragging, onSlideStopDragging } = this.props;
                    const localPoint = this.calculateLocalPoint(clientX, clientY);
                    const x = localPoint.x + slideDragging.offsetX;
                    const y = localPoint.y + slideDragging.offsetY;
                    if (x < 0 || x > width - SLIDE_WIDTH / 2 || y < 0 || y > height - SLIDE_HEIGHT / 2) {
                        onSlideStopDragging(
                            Math.max(0, Math.min(x, width - SLIDE_WIDTH / 2)),
                            Math.max(0, Math.min(y, height - SLIDE_HEIGHT / 2))
                        );
                    } else {
                        onSlideDragging(x, y);
                    }
                } else if (sketchState) {
                    const { onShapeSketching, onShapeStopSketching } = this.props;
                    const localPoint = this.calculateLocalPoint(clientX, clientY);
                    const x = localPoint.x + sketchState.offsetX;
                    const y = localPoint.y + sketchState.offsetY;
                    if (x < 0 || x > SLIDE_WIDTH || y < 0 || y > SLIDE_CONTENT_HEIGHT) {
                        onShapeStopSketching(
                            Math.max(0, Math.min(x, SLIDE_WIDTH)),
                            Math.max(0, Math.min(y, SLIDE_CONTENT_HEIGHT))
                        );
                    } else {
                        onShapeSketching(x, y);
                    }
                } else if (shapeDragging) {
                    const { onShapeDragging, onShapeStopDragging } = this.props;
                    const localPoint = this.calculateLocalPoint(clientX, clientY);
                    const { offsetX, offsetY, boundsOffsetX, boundsOffsetY } = shapeDragging;

                    const xMin = Math.max(0, boundsOffsetX);
                    const yMin = Math.max(0, boundsOffsetY);
                    const xMax = Math.min(SLIDE_WIDTH, SLIDE_WIDTH + boundsOffsetX);
                    const yMax = Math.min(SLIDE_CONTENT_HEIGHT, SLIDE_CONTENT_HEIGHT + boundsOffsetY);

                    const x = localPoint.x + offsetX;
                    const y = localPoint.y + offsetY;

                    const xBound = Math.max(xMin, Math.min(x, xMax));
                    const yBound = Math.max(yMin, Math.min(y, yMax));

                    if (x < xMin - SLIDE_WIDTH / 4 || x > xMax + SLIDE_WIDTH / 4 || y < yMin - SLIDE_CONTENT_HEIGHT / 4 || y > yMax + SLIDE_CONTENT_HEIGHT / 4) {
                        onShapeStopDragging(xBound, yBound);
                    } else {
                        onShapeDragging(xBound, yBound);
                    }
                } else if (selectorDragging) {
                    const { onSelectorDragging, onSelectorStopDragging } = this.props;
                    const localPoint = this.calculateLocalPoint(clientX, clientY);

                    const x = localPoint.x + selectorDragging.offsetX;
                    const y = localPoint.y + selectorDragging.offsetY;

                    const xBound = Math.max(0, Math.min(x, SLIDE_WIDTH));
                    const yBound = Math.max(0, Math.min(y, SLIDE_CONTENT_HEIGHT));

                    if (x < - SLIDE_WIDTH / 4 || x > SLIDE_WIDTH + SLIDE_WIDTH / 4 || y < - SLIDE_CONTENT_HEIGHT / 4 || y > SLIDE_CONTENT_HEIGHT + SLIDE_CONTENT_HEIGHT / 4) {
                        onSelectorStopDragging(xBound, yBound);
                    } else {
                        onSelectorDragging(xBound, yBound);
                    }
                } else if (panning) {
                    const { width, height, onPanning, onStopPanning } = this.props;
                    if (clientX <= 0 || clientX >= width || clientY <= 0 || clientY >= height) {
                        onStopPanning(Math.max(0, Math.min(clientX, width)), Math.max(0, Math.min(clientY, height)));
                    } else {
                        onPanning(clientX, clientY);
                    }
                }
            }
        );
        this.scheduleScroll = rafSchedule(
            () => {
                const background = this.background;
                if (background) {
                    this.props.onScrolled(background.scrollLeft, background.scrollTop);
                }
            }
        );

        // @ts-ignore
        const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;
        this.resizeObserver = new ResizeObserver(() => {
            const background = this.background;
            if (background) {
                const rect = background.getBoundingClientRect();
                this.props.onStoryboardResized(rect.width, rect.height);
            }
        });
    }

    public componentDidMount() {
        const background = this.background;
        if (background) {
            this.props.onStoryboardResized(background.clientWidth, background.clientHeight);
            this.resizeObserver.observe(background);
        }
    }

    public componentWillUnmount() {
        this.scheduleMouseMove.cancel();
        this.scheduleScroll.cancel();
        this.resizeObserver.disconnect();
    }

    public componentWillUpdate(nextProps: Props) {
        const background = this.background;

        const oldScrollLeft = this.props.scrollLeft;
        const newScrollLeft = nextProps.scrollLeft;
        if (background && oldScrollLeft !== newScrollLeft) {
            background.scrollLeft = newScrollLeft;
        }

        const oldScrollTop = this.props.scrollTop;
        const newScrollTop = nextProps.scrollTop;
        if (background && oldScrollTop !== newScrollTop) {
            background.scrollTop = newScrollTop;
        }
    }

    public render() {
        const {
            width,
            height,
            classes,
            notes,
            slides,
            panning,
            noteDragging,
            shapeDragging,
            slideDragging,
            templateData,
            templateTextSupplier,
            textFields,
            zoomLevel,
            onCanvasClicked,
            onNoteDeleteButtonClicked,
            onNoteDoubleClicked,
            onBackgroundDoubleClicked,
            onTemplateHelpClicked,
            onTextFieldClicked
        } = this.props;
        return (
            <div
                className={classes.root}
                ref={element => this.background = element}
                onScroll={this.handleScroll}
            >
                <svg
                    ref={(element) => this.svg = element}
                    className={noteDragging || slideDragging || shapeDragging || panning? 'cursorGrabbing' : 'cursorDefault'}
                    width={`${zoomLevel * 100}%`}
                    height={`${zoomLevel * 100}%`}
                    viewBox={`0 0 ${width} ${height}`}
                    onMouseDown={this.handleMouseDown}
                    onMouseMove={this.handleMouseMove}
                    onMouseUp={this.handleMouseUp}
                    onClick={onCanvasClicked}
                    onDoubleClick={onBackgroundDoubleClicked}
                >

                    <NoteDefs/>

                    <rect fill="white" width={width} height={height}/>

                    <StoryboardTemplateView
                        templateData={templateData}
                        textFields={textFields}
                        templateTextSupplier={templateTextSupplier}
                        onHelpClicked={onTemplateHelpClicked}
                        onTextFieldClicked={onTextFieldClicked}
                    />

                    {slides.map((slide, index) =>
                        <Slide
                            key={index}
                            slide={slide}
                            calculateSVGPoint={this.calculateLocalPoint}
                        />
                    )}

                    {notes.map((note, index) =>
                        <NoteView
                            key={index}
                            {...note}
                            dragging={noteDragging ? noteDragging.uuid === note.uuid : false}
                            onMouseDown={(e: React.MouseEvent<SVGElement>) => this.handleNoteMouseDown(note.uuid, e)}
                            onDoubleClicked={() => onNoteDoubleClicked(note.uuid, note.text, note.color)}
                            onDeleteButtonClicked={() => onNoteDeleteButtonClicked(note.uuid)}
                        />
                    )}
                </svg>
            </div>
        );
    }

    private readonly calculateLocalPoint = (x: number, y: number) => {
        if (this.svg) {
            const point = this.svg.createSVGPoint();
            point.x = x;
            point.y = y;
            return point.matrixTransform(this.svg.getScreenCTM().inverse());
        }
        return { x: 0, y: 0 };
    };

    private readonly handleScroll = () => {
        this.scheduleScroll();
    };


    private handleNoteMouseDown(uuid: string, e: React.MouseEvent<SVGElement>) {
        stopPropagation(e);
        const { onNoteStartDragging } = this.props;
        const localPoint = this.calculateLocalPoint(e.clientX, e.clientY);
        onNoteStartDragging(uuid, localPoint.x, localPoint.y);
    }

    private readonly handleMouseDown = (e: React.MouseEvent<SVGElement>) => {
        this.props.onStartPanning(e.clientX, e.clientY);
    };

    private readonly handleMouseMove = (e: React.MouseEvent<SVGElement>) => {
        this.scheduleMouseMove(e.clientX, e.clientY);
    };

    private readonly handleMouseUp = (e: React.MouseEvent<SVGElement>) => {
        const { noteDragging, slideDragging, sketchState, shapeDragging, selectorDragging, panning} = this.props;
        if (noteDragging) {
            const { width, height, onNoteStopDragging } = this.props;
            const localPoint = this.calculateLocalPoint(e.clientX, e.clientY);
            const x = localPoint.x + noteDragging.offsetX;
            const y = localPoint.y + noteDragging.offsetY;
            onNoteStopDragging(
                Math.max(-NOTE_SIZE / 4, Math.min(x, width - NOTE_SIZE / 2)),
                Math.max(-NOTE_SIZE / 4, Math.min(y, height - NOTE_SIZE / 2))
            );
        } else if (slideDragging) {
            const { width, height, onSlideStopDragging } = this.props;
            const localPoint = this.calculateLocalPoint(e.clientX, e.clientY);
            const x = localPoint.x + slideDragging.offsetX;
            const y = localPoint.y + slideDragging.offsetY;
            onSlideStopDragging(
                Math.max(0, Math.min(x, width - SLIDE_WIDTH / 2)),
                Math.max(0, Math.min(y, height - SLIDE_HEIGHT / 2))
            );
        } else if (sketchState) {
            const localPoint = this.calculateLocalPoint(e.clientX, e.clientY);
            const x = localPoint.x + sketchState.offsetX;
            const y = localPoint.y + sketchState.offsetY;
            this.props.onShapeStopSketching(
                Math.max(0, Math.min(x, SLIDE_WIDTH)),
                Math.max(0, Math.min(y, SLIDE_CONTENT_HEIGHT))
            );
        } else if (shapeDragging) {
            const localPoint = this.calculateLocalPoint(e.clientX, e.clientY);
            const { offsetX, offsetY, boundsOffsetX, boundsOffsetY } = shapeDragging;

            const xMin = Math.max(0, boundsOffsetX);
            const yMin = Math.max(0, boundsOffsetY);
            const xMax = Math.min(SLIDE_WIDTH, SLIDE_WIDTH + boundsOffsetX);
            const yMax = Math.min(SLIDE_CONTENT_HEIGHT, SLIDE_CONTENT_HEIGHT + boundsOffsetY);

            const x = localPoint.x + offsetX;
            const y = localPoint.y + offsetY;
            this.props.onShapeStopDragging(
                Math.max(xMin, Math.min(x, xMax)),
                Math.max(yMin, Math.min(y, yMax))
            );
        } else if (selectorDragging) {
            const localPoint = this.calculateLocalPoint(e.clientX, e.clientY);
            const x = localPoint.x + selectorDragging.offsetX;
            const y = localPoint.y + selectorDragging.offsetY;
            this.props.onSelectorStopDragging(
                Math.max(0, Math.min(x, SLIDE_WIDTH)),
                Math.max(0, Math.min(y, SLIDE_CONTENT_HEIGHT))
            );
        } else if (panning) {
            const { width, height, onStopPanning } = this.props;
            onStopPanning(
                Math.max(0, Math.min(e.clientX, width)),
                Math.max(0, Math.min(e.clientY, height))
            );
        }
    };
}

export default withStyles(styles)(Storyboard);



// WEBPACK FOOTER //
// ./src/components/StoryboardView.tsx